import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexGrid,
    ApexMarkers,
    ApexStates,
    ApexStroke,
    ApexTitleSubtitle,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
    ChartComponent,
} from 'ng-apexcharts';
import { MdrDashboardsService } from '../../../services/mdr-dashboards.service';
import moment from 'moment';
import { TimeframeService } from '../../../../timeframe/services/timeframe.service';

export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    dataLabels: ApexDataLabels;
    grid: ApexGrid;
    stroke: ApexStroke;
    title: ApexTitleSubtitle;
    yaxis: ApexYAxis;
    tooltip: ApexTooltip;
    states?: ApexStates;
    markers?: ApexMarkers;
};

@Component({
    selector: 'app-mdr-dashboard-spike',
    templateUrl: './mdr-dashboard-spike.component.html',
    styleUrls: ['./mdr-dashboard-spike.component.scss'],
    standalone: false,
})
export class MdrDashboardSpikeComponent implements OnInit, OnChanges {
    @ViewChild('chart') chart: ChartComponent;
    @Input() element: any;
    @Input() strokeColors: string[] = [];
    @Input() chartColor: string;
    @Input() chooseFromDataInfo: boolean;
    @Input() chooseNameFromData: string;
    public chartOptions: ChartOptions;
    public data: number[] = [];
    public categories: any[] = [];
    public allDataEmpty: boolean;
    public bulletType = 'square';
    public tooltipWidth = 220;

    constructor(public mdrDashboardService: MdrDashboardsService, public timeframeService: TimeframeService) {}

    ngOnInit(): void {
        let series = [];
        this.data = [];
        for (let i = 0; i < this.element.length; i++) {
            this.data.push(this.element[i][1]);
            // use reduce() method to find the sum
            this.allDataEmpty =
                this.data.reduce((accumulator, currentValue) => {
                    return accumulator + currentValue;
                }, 0) === 0;
            this.categories.push(new Date(this.element[i][0]));
        }
        this.chartOptions = {
            series: [
                {
                    name: '',
                    data: this.data,
                },
            ],
            chart: {
                sparkline: {
                    enabled: true,
                },
                height: '29px',
                type: 'line',
                zoom: {
                    enabled: false,
                },
            },
            markers: {
                size: 0,
            },
            dataLabels: {
                enabled: false,
            },
            stroke: {
                curve: 'straight',
                width: 2,
                colors: [this.chartColor || ' #67b7dc'],
            },
            title: {},
            grid: {
                show: false,
                xaxis: {
                    lines: {
                        show: false,
                    },
                },
                yaxis: {
                    lines: {
                        show: false,
                    },
                },
            },
            xaxis: {
                tooltip: {
                    enabled: false,
                },
                axisTicks: {
                    show: false,
                },
                labels: {
                    formatter: function (val: any, timestamp: any, element: any) {
                        if (this.timeframeService.currentlySelectedTimeframe()?.name === '24h') {
                            return moment(timestamp).format('HH:mm');
                        }

                        if (this.timeframeService.currentlySelectedTimeframe()?.name === '7d') {
                            return moment(timestamp).format('DD MMM');
                        }

                        if (this.timeframeService.currentlySelectedTimeframe()?.name === '30d') {
                            return moment(timestamp).format('DD MMM');
                        }

                        return '';
                    }.bind(this),
                    hideOverlappingLabels: false,
                    style: {
                        colors: '#00000080',
                        fontSize: '10px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 400,
                    },
                },
                categories: this.categories,
            },
            yaxis: {
                labels: {
                    style: {
                        colors: '#00000080',
                        fontSize: '10px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 400,
                    },
                    formatter: function (val: number) {
                        if (val === Infinity) {
                            return 1;
                        }
                        return this.mdrDashboardsService.intToString(val ? Number(val.toFixed(0)) : 0, 1000, this.element.approximate);
                    }.bind(this),
                },
            },
            tooltip: {
                shared: true,
                custom: ({
                    series,
                    seriesIndex,
                    dataPointIndex,
                    w,
                }: {
                    series: [];
                    seriesIndex: number;
                    dataPointIndex: number;
                    w: any;
                }) => {
                    let hoverList = '';
                    /* if the selected timeframe is set to 24h, add hour and minute representation */
                    let formattedDate = new Date(this.categories[dataPointIndex])
                        .toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                        })
                        .replace(/ /g, ' ');

                    if (w.globals.series[0][dataPointIndex]) {
                        hoverList =
                            `
                                    <div class='gz-data-item'>
                                        <span class='${this.bulletType}-bullet-legend' style='background: ${this.strokeColors[0]}'></span><br />
                                        <div class='gz-data-item--title'>${this.chooseNameFromData}</div>
                                        <div class='gz-data-item--value'>${w.globals.series[0][dataPointIndex]}</div>
                                    </div>
                            ` + hoverList;
                        return `<div class='chart-tooltip-container' style='min-width: ${this.tooltipWidth}px'>
                                <div class='chart-tooltip-title'>${formattedDate}</div>
                                ${hoverList}
                            </div>`;
                    }

                    return '';
                },
            },
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.element && changes.element.previousValue !== changes.element.currentValue) {
            this.element = changes.element.currentValue;
        }
    }
}
